// Does the signed-in person have a practice? (PRD 40 §5.2)
//
// Read by the sidebar, to decide whether to draw the Practice section, and by
// the landing redirect, to decide where "/" goes. Both go through the store so
// the question costs one request per sign-in however many callers ask it.
//
// UNKNOWN MEANS DO NOT DRAW, which is the OPPOSITE of the rule PRD 45 and
// PRD 50 apply next door — and deliberately so. Those two SUBTRACT rows, where
// guessing wrong hides something the customer has; this one ADDS a section,
// where guessing wrong offers a practice screen to somebody with no practice.
// Being briefly incomplete is recoverable; being briefly wrong is a dead link.
// The mirror is what keeps "briefly" down to the first visit per browser.

import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  FirmMembershipState,
  clearFirmMembership,
  ensureFirmMembershipLoaded,
  getFirmMembershipState,
  FIRM_ROLE_OWNER,
  FIRM_ROLE_ADMIN,
  readFirmMirror,
  readRoleMirror,
  readWorkflowMirror,
  subscribeFirmMembership,
} from '../services/firmMembershipStore';

/**
 * True when the user holds at least one active firm membership; false when
 * they hold none; null while that is still unknown.
 *
 * Callers that can afford to wait for null to resolve (the landing redirect)
 * should. Callers that cannot (the sidebar) treat it as false.
 */
export function useBelongsToFirm(): boolean | null {
  const { user } = useAuth();
  const uuid = user?.uuid;
  const authToken = user?.authToken;

  const [state, setState] = useState<FirmMembershipState>(() => {
    // Seed from the mirror in the initialiser, not in the effect: an effect
    // runs after the first paint, which is one frame of a missing Practice
    // section for every practitioner on every page load.
    const current = getFirmMembershipState();
    if (current.belongsToFirm !== null) return current;
    return {
      belongsToFirm: readFirmMirror(uuid),
      practiceWorkflow: readWorkflowMirror(uuid),
      firmRole: readRoleMirror(uuid),
      hasLoaded: false,
    };
  });

  useEffect(() => {
    const unsubscribe = subscribeFirmMembership(setState);
    if (authToken) {
      ensureFirmMembershipLoaded(uuid);
      // The store may already hold a settled answer from a previous mount;
      // adopt it rather than waiting for the next change to be published.
      setState(getFirmMembershipState());
    } else {
      clearFirmMembership();
    }
    return unsubscribe;
  }, [authToken, uuid]);

  // No session, so no practice — and nothing to wait for. ProtectedRoute
  // makes this unreachable for both real callers, which is exactly why it is
  // written down: without it, "signed out" and "still asking" would be the
  // same null, and LandingRedirect waits on that null forever.
  //
  // Not folded into the store, because the store's null has to keep meaning
  // "unknown": signing in as somebody else must re-ask rather than inherit
  // the previous person's answer, and a settled false is not re-asked.
  if (!authToken) return false;

  return state.belongsToFirm;
}

/**
 * Is practice workflow (PRD 61) switched on for this deployment?
 *
 * Shares the store with `useBelongsToFirm` because both answers ride the same
 * `GET /home`, which the app already blocks on. Same "unknown means do not
 * draw" rule, and for a sharper reason: this one is a ROLLOUT flag, so a wrong
 * guess offers a screen the server will 404.
 *
 * Deliberately does NOT trigger its own fetch. `belongsToFirm` has a fallback
 * endpoint (`GET /firm/firms`) for APIs that predate it; a rollout flag has no
 * such endpoint, so absence settles as off rather than as a question.
 */
export function usePracticeWorkflow(): boolean | null {
  const { user } = useAuth();
  const uuid = user?.uuid;
  const authToken = user?.authToken;

  const [state, setState] = useState<FirmMembershipState>(() => {
    const current = getFirmMembershipState();
    if (current.practiceWorkflow !== null) return current;
    return {
      belongsToFirm: readFirmMirror(uuid),
      practiceWorkflow: readWorkflowMirror(uuid),
      firmRole: readRoleMirror(uuid),
      hasLoaded: false,
    };
  });

  useEffect(() => {
    const unsubscribe = subscribeFirmMembership(setState);
    if (authToken) {
      ensureFirmMembershipLoaded(uuid);
      setState(getFirmMembershipState());
    } else {
      clearFirmMembership();
    }
    return unsubscribe;
  }, [authToken, uuid]);

  if (!authToken) return false;

  return state.practiceWorkflow;
}

/**
 * Does the signed-in person OWN the practice the app has open? (PRD 40 §5.2)
 *
 * Same store and the same "unknown means do not draw" rule as the two above:
 * this ADDS a row, so guessing wrong would offer an owner-only screen to an
 * admin — and though the API refuses them, a menu row that 403s is a worse
 * answer than no row.
 *
 * NOT AN AUTHORISATION CHECK. `require_firm_role!` is, on every endpoint. This
 * only decides what to draw.
 */
export function useIsFirmOwner(): boolean | null {
  const { user } = useAuth();
  const uuid = user?.uuid;
  const authToken = user?.authToken;

  const [state, setState] = useState<FirmMembershipState>(() => {
    const current = getFirmMembershipState();
    if (current.firmRole !== null) return current;
    return {
      belongsToFirm: readFirmMirror(uuid),
      practiceWorkflow: readWorkflowMirror(uuid),
      firmRole: readRoleMirror(uuid),
      hasLoaded: false,
    };
  });

  useEffect(() => {
    const unsubscribe = subscribeFirmMembership(setState);
    if (authToken) {
      ensureFirmMembershipLoaded(uuid);
      setState(getFirmMembershipState());
    } else {
      clearFirmMembership();
    }
    return unsubscribe;
  }, [authToken, uuid]);

  if (!authToken) return false;
  // Null stays null: the caller decides whether "still asking" draws.
  return state.firmRole === null ? null : state.firmRole === FIRM_ROLE_OWNER;
}

/**
 * Owner or admin — the pair that RUNS the practice rather than works in it,
 * and the same pair every `MANAGING_ROLES` gate on the api names.
 *
 * Used to decide where a firm member lands (PRD 66 §5.1) and whether to draw
 * the Dashboard row. Null while unknown, and callers WAIT on null rather than
 * guessing: guessing staff sends an owner somewhere they did not ask for, and
 * guessing manager sends a staff member to a screen the api will refuse.
 *
 * NOT AN AUTHORISATION CHECK. `require_firm_role!` is, on every endpoint.
 */
export function useIsFirmManager(): boolean | null {
  const role = useFirmRole();
  return role === null ? null : role === FIRM_ROLE_OWNER || role === FIRM_ROLE_ADMIN;
}

/** The raw role, for callers that need to tell owner from admin. */
export function useFirmRole(): string | null {
  const { user } = useAuth();
  const uuid = user?.uuid;
  const authToken = user?.authToken;

  const [state, setState] = useState<FirmMembershipState>(() => {
    const current = getFirmMembershipState();
    if (current.firmRole !== null) return current;
    return {
      belongsToFirm: readFirmMirror(uuid),
      practiceWorkflow: readWorkflowMirror(uuid),
      firmRole: readRoleMirror(uuid),
      hasLoaded: false,
    };
  });

  useEffect(() => {
    const unsubscribe = subscribeFirmMembership(setState);
    if (authToken) {
      ensureFirmMembershipLoaded(uuid);
      setState(getFirmMembershipState());
    } else {
      clearFirmMembership();
    }
    return unsubscribe;
  }, [authToken, uuid]);

  if (!authToken) return null;
  return state.firmRole;
}
